// eslint-disable-next-line import/no-extraneous-dependencies
import { Node, mergeAttributes } from '@tiptap/core';
// eslint-disable-next-line import/no-extraneous-dependencies
import { VueNodeViewRenderer } from '@tiptap/vue-3';
import RawHtmlBlockView from './RawHtmlBlockView.vue';

/**
 * RawHtmlBlock — catch-all Tiptap node for preserving custom HTML that
 * ProseMirror's schema would otherwise strip (review cards, styled divs,
 * inline ad variants, etc.).
 *
 * Stores the original outerHTML as a string attribute and renders it in
 * a read-only NodeView container. On getHTML(), the parent editor's onUpdate
 * post-processes the output to replace placeholder divs with the raw HTML.
 *
 * KEY DESIGN: The attribute parseHTML handles both cases:
 *   1. Initial parse from API HTML — element has no data-raw-html, so we
 *      capture element.outerHTML directly.
 *   2. Re-parse of editor output — element IS our wrapper div with
 *      data-raw-html attribute, so we decode from that.
 *
 * Priority is set lower (40) than specific nodes like AdBlockNode and
 * ComparisonSliderNode (default 50) so those match first. An explicit
 * rule for our own wrapper div uses priority 60 to always re-parse correctly.
 */

// Classes handled by dedicated extensions — skip these in the catch-all
const KNOWN_CLASSES = [
  'comparison-slide-card',
  'article-in-ad-block',
  'ProseMirror',
];

const RawHtmlBlock = Node.create({
  name: 'rawHtmlBlock',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      rawHtml: {
        default: '',
        renderHTML: (attributes) => ({
          'data-raw-html': encodeURIComponent(attributes.rawHtml || ''),
        }),
        // Handles both parsing scenarios:
        // - Re-parsing editor output: data-raw-html exists → decode it
        // - Initial API HTML parse: no data-raw-html → capture outerHTML
        parseHTML: (element) => {
          const encoded = element.getAttribute('data-raw-html');
          if (encoded) return decodeURIComponent(encoded);
          return element.outerHTML;
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        // Re-parsing our own output — high priority to match first
        tag: 'div.raw-html-block[data-raw-html]',
        priority: 60,
      },
      {
        // Catch divs with inline styles not handled by other extensions
        tag: 'div[style]',
        priority: 40,
        getAttrs: (element) => {
          const classList = Array.from(element.classList || []);
          if (KNOWN_CLASSES.some((cls) => classList.includes(cls))) return false;
          return {};
        },
      },
      {
        // Catch divs with classes not handled by other extensions
        tag: 'div[class]',
        priority: 40,
        getAttrs: (element) => {
          const classList = Array.from(element.classList || []);
          if (KNOWN_CLASSES.some((cls) => classList.includes(cls))) return false;
          if (!element.getAttribute('style') && classList.length === 0) return false;
          return {};
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { class: 'raw-html-block' }),
    ];
  },

  addNodeView() {
    return VueNodeViewRenderer(RawHtmlBlockView);
  },
});

export default RawHtmlBlock;
